export interface WhatsAppError {
  message: string;
  code?: string;
  details?: unknown;
}

// WhatsApp Session Status Response
export interface WhatsAppSessionStatusResponse {
  name: string;
  status: WhatsAppChannelStatus;
  config: WhatsAppSessionConfig;
  me?: {
    id: string; // Phone number ID (e.g., "31631229410@c.us")
    pushName: string; // Business account name
  };
  engine?: {
    engine: string;
    WWebVersion: string;
    state: 'CONNECTED' | 'DISCONNECTED';
  };
}

// WhatsApp Channel Types
export type WhatsAppChannelStatus =
  | 'WORKING'
  | 'CONNECTED'
  | 'PENDING'
  | 'FAILED'
  | 'STARTING'
  | 'STOPPED'
  | 'SCAN_QR_CODE'
  | 'STOPPING'
  | 'INITIALIZING';

// WhatsApp Session Types
export interface WhatsAppSessionConfig {
  metadata: {
    'user.id': string;
  };
  proxy: null;
  debug: boolean;
}

// WhatsApp Session Response
export interface WhatsAppSessionResponse {
  id: string;
  name: string;
  status: WhatsAppChannelStatus;
  config: WhatsAppSessionConfig;
  webhooks: {
    url: string;
    events: string[];
    retries: number;
  }[];
}

// Create WhatsApp Session Request
export interface CreateWhatsAppSessionRequest {
  name: string;
  start: boolean;
  config: WhatsAppSessionConfig;
  webhooks: {
    url: string;
    events: string[];
    retries: number;
  }[];
}

// Add a utility if needed to extract phone numbers from WhatsApp format
export function extractPhoneNumber(whatsappId: string): string {
  // Regex to extract phone number from formats like:
  // "1234567890@c.us" or "whatsapp:1234567890"
  const match = whatsappId.match(/(\d+)[@:]?/);
  return match ? match[1] : whatsappId;
}

// === WAHA API Interfaces ===

// Message Interfaces
export interface WAMessageBase {
  session?: string;
  chatId: string;
}

export interface WATextMessage extends WAMessageBase {
  text: string;
  quotedMessageId?: string;
}

export interface WAMediaMessage extends WAMessageBase {
  caption?: string;
  file: {
    mimetype: string;
    filename?: string;
    data?: string; // base64 data
    url?: string; // remote URL
  };
}

export interface WALocationMessage extends WAMessageBase {
  latitude: number;
  longitude: number;
  title?: string;
}

export interface WAButtonMessage extends WAMessageBase {
  text: string;
  footer?: string;
  buttons: {
    id: string;
    text: string;
  }[];
}

export interface WAPollMessage extends WAMessageBase {
  title: string;
  options: string[];
  multipleAnswers?: boolean;
}

// Response interfaces
export interface WAMessageResponse {
  id: string;
  timestamp: number;
  from: string;
  fromMe: boolean;
  to: string;
  body?: string;
  type?: string;
  hasMedia?: boolean;
  mediaUrl?: string;
}

// Contact interfaces
export interface WAContact {
  id: string;
  name?: string;
  shortName?: string;
  pushname?: string;
  isGroup?: boolean;
  isWAContact?: boolean;
  isMyContact?: boolean;
  isBlocked?: boolean;
}

export interface WAContactCheckResponse {
  id: string;
  exists: boolean;
  isRegistered: boolean;
  status?: string;
}

// Session webhook/event interfaces
export interface WAHAWebhookEvent {
  id: string;
  timestamp: number;
  session: string;
  event: WAHAEventType;
  payload: any;
}

export type WAHAEventType =
  | 'session.status'
  | 'message'
  | 'message.any'
  | 'message.ack'
  | 'message.reaction'
  | 'group.join'
  | 'group.leave'
  | 'presence.update'
  | 'state.change';

export interface WAHAPresenceUpdate {
  id: string;
  lastKnownPresence:
    | 'unavailable'
    | 'available'
    | 'composing'
    | 'recording'
    | 'paused';
}

// Chat interfaces
export interface WAChat {
  id: string;
  name: string;
  timestamp: number;
  isGroup: boolean;
  unreadCount: number;
  pinned?: boolean;
  muteExpiration?: number;
}

// Group interfaces
export interface WAGroupCreate {
  name: string;
  participants: string[];
  session?: string;
}

export interface WAGroupUpdate {
  groupId: string;
  subject?: string;
  description?: string;
  session?: string;
}

export interface WAGroupParticipants {
  groupId: string;
  participants: string[];
  action: 'add' | 'remove' | 'promote' | 'demote';
  session?: string;
}

export interface WAGroupResponse {
  id: string;
  name: string;
  participants: {
    id: string;
    isAdmin: boolean;
    isSuperAdmin: boolean;
  }[];
  description?: string;
  owner?: string;
  creation?: number;
}

// Reactions
export interface WAReaction {
  messageId: string;
  reaction: string; // emoji or empty string to remove
  session?: string;
}

// Typing and presence
export interface WATypingRequest {
  chatId: string;
  session?: string;
}

export interface WAPresenceRequest {
  presence: 'available' | 'unavailable' | 'composing' | 'recording' | 'paused';
  session?: string;
}

// Media download
export interface WAMediaDownloadRequest {
  messageId: string;
  session?: string;
}

export interface WAMediaDownloadResponse {
  mimetype: string;
  data: string; // base64
  filename?: string;
}

// Status (stories)
export type WAStatusMessage = WAMediaMessage;

// Message forwarding
export interface WAForwardMessage {
  chatId: string;
  messageId: string;
  session?: string;
}
